// SignInPage.js

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSpring, animated } from "react-spring";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationCircle,
  faCheckCircle,
  faUser,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import logo from "../assets/logo.jpg";
// If you're using a user context or similar from your project, import it here
import { useUser } from "ogcommon";

function SignInPage() {
  const navigate = useNavigate();
  const { setUser } = useUser(); // Or whatever your context method is called

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // If you still wish to extract subdomain, keep this; otherwise remove it
  const [subdomain, setSubdomain] = useState("");

  // Simple fade/slide-in animation for the form
  const formAnimation = useSpring({
    opacity: 1,
    transform: "translateY(0)",
    from: { opacity: 0, transform: "translateY(50px)" },
    config: { mass: 1, tension: 200, friction: 20 },
  });

  // Example of extracting subdomain from current hostname
  useEffect(() => {
    const extractSubdomain = () => {
      const hostname = window.location.hostname;
      const parts = hostname.split(".");
      if (parts.length > 2) {
        const subdomainPart = parts.shift();
        setSubdomain(subdomainPart);
      } else {
        setSubdomain("");
      }
    };

    extractSubdomain();
  }, []);

  // Example: hashing password before storing offline
  function hashPassword(password) {
    let hash = 0,
      i,
      chr;
    for (i = 0; i < password.length; i++) {
      chr = password.charCodeAt(i);
      hash = (hash << 5) - hash + chr;
      hash |= 0; // Convert to 32-bit integer
    }
    return hash.toString();
  }

  // Handle password reset
  const handleForgotPassword = async () => {
    if (username.trim() === "") {
      setError("Please Enter Username");
      return;
    }

    try {
      // Adjust baseUrl as needed
      const hostname = window.location.hostname;
      const parts = hostname.split(".");
      let baseUrl;

      if (parts.length > 2) {
        parts.shift(); // remove subdomain if present
        baseUrl = `https://house.ogendeavors.com`;
      } else {
        baseUrl = "https://house.ogendeavors.com";
      }

      const response = await fetch(`${baseUrl}/api/passwordreset.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userID: username }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage("Password reset email sent successfully");
        setError("");
      } else {
        setError("Failed to send password reset email");
        setSuccessMessage("");
      }
    } catch (error) {
      setError("An error occurred while sending password reset email.");
      setSuccessMessage("");
    }
  };

  // Handle sign in
  async function handleSignIn(e) {
    e.preventDefault();

    try {
      // Adjust baseUrl as needed
      const hostname = window.location.hostname;
      const parts = hostname.split(".");
      let baseUrl;

      if (parts.length > 2) {
        parts.shift(); // remove subdomain if present
        baseUrl = `https://house.ogendeavors.com`;
      } else {
        baseUrl = "https://house.ogendeavors.com";
      }

      // If you're offline, try offline sign-in
      if (!navigator.onLine) {
        handleOfflineSignIn();
        return;
      }

      const response = await fetch(`${baseUrl}/api/login_api.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      const { success, message, admin_user } = data;

      if (success) {
        // If using a context, set user data accordingly
        // Or store it in localStorage if you prefer
        setUser({
          userID: admin_user.UserID,
          sec: admin_user.Sec,
          name: admin_user.Name,
          phone: admin_user.Phone,
          address: admin_user.Address,
          cityStateZip: admin_user.CityStateZip,
        });

        // Example storing in localStorage
        localStorage.setItem("admin_user", JSON.stringify(admin_user));

        // If you wish to store credentials offline
        localStorage.setItem(
          "credentials",
          JSON.stringify({
            username,
            password: hashPassword(password),
            admin_user,
          })
        );

        // Navigate to homepage
        navigate("/home");
      } else {
        setError(message);
      }
    } catch (error) {
      setError("An error occurred while signing in.");
    }
  }

  // Handle offline sign-in from localStorage if needed
  function handleOfflineSignIn() {
    const storedCredentials = JSON.parse(localStorage.getItem("credentials"));
    if (storedCredentials) {
      const {
        username: storedUsername,
        password: storedPasswordHash,
        admin_user,
      } = storedCredentials;
      const enteredPasswordHash = hashPassword(password);

      if (
        username === storedUsername &&
        enteredPasswordHash === storedPasswordHash
      ) {
        setUser(admin_user); // or setUser(...) as needed
        localStorage.setItem("admin_user", JSON.stringify(admin_user));
        navigate("/home");
      } else {
        setError("Invalid credentials. Please sign in online first.");
      }
    } else {
      setError("No saved credentials available. Please sign in online first.");
    }
  }

  return (
    <div className="min-h-screen flex flex-col justify-center items-center relative overflow-hidden bg-gradient-to-r from-gray-100 to-gray-200">
      <animated.div
        style={formAnimation}
        className="bg-white shadow-2xl rounded-lg px-6 py-10 sm:px-12 sm:py-14 max-w-md w-full transform hover:scale-105 transition-transform duration-500 relative z-10 flex flex-col justify-center min-h-screen sm:min-h-0"
      >
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-gray-400 to-gray-500 rounded-t-lg animate-pulse"></div>
        <img
          src={logo}
          className="w-40 sm:w-32 mx-auto mb-6 sm:mb-8 rounded-full border-4 border-white shadow-md animate-float"
          alt="logo"
        />

        {subdomain && (
          <p className="text-xl font-semibold text-gray-800 text-center mb-6">
            {subdomain.charAt(0).toUpperCase() + subdomain.slice(1)}
          </p>
        )}

        <form className="space-y-6 sm:space-y-8" onSubmit={handleSignIn}>
          <div>
            <label
              className="block text-gray-700 font-semibold mb-2 text-base sm:text-lg"
              htmlFor="username"
            >
              <FontAwesomeIcon icon={faUser} className="mr-2" />
              Username
            </label>
            <input
              className="w-full border-gray-300 border-2 rounded-md px-4 py-2 sm:px-5 sm:py-3 text-base sm:text-lg focus:outline-none focus:ring-4 focus:ring-gray-400 focus:border-transparent transition-all duration-300"
              id="username"
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div>
            <label
              className="block text-gray-700 font-semibold mb-2 text-base sm:text-lg"
              htmlFor="password"
            >
              <FontAwesomeIcon icon={faLock} className="mr-2" />
              Password
            </label>
            <input
              className="w-full border-gray-300 border-2 rounded-md px-4 py-2 sm:px-5 sm:py-3 text-base sm:text-lg focus:outline-none focus:ring-4 focus:ring-gray-400 focus:border-transparent transition-all duration-300"
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {successMessage && (
            <p className="text-green-500 text-base sm:text-lg animate-pulse">
              <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
              {successMessage}
            </p>
          )}
          {error && (
            <p className="text-red-500 text-base sm:text-lg animate-pulse">
              <FontAwesomeIcon icon={faExclamationCircle} className="mr-2" />
              {error}
            </p>
          )}

          <button
            className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-full hover:from-gray-700 hover:to-gray-800 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-gray-600 transform hover:scale-105 transition-transform duration-500 shadow-lg text-lg sm:text-xl"
            type="submit"
          >
            Sign In
          </button>

          <p className="text-gray-600 text-center text-base sm:text-lg">
            <button
              className="text-gray-700 hover:text-gray-800 underline transition-colors duration-300"
              type="button"
              onClick={handleForgotPassword}
            >
              Forgot Password?
            </button>
          </p>
        </form>
      </animated.div>
    </div>
  );
}

export default SignInPage;
